import {customerId as getCustomerId} from "./services/LoginService";
import {del, get, post} from "./Requester";

export type DirectErp = "invoiz" | "wisomeinbuero";

// The customer-facing connect flow is OAuth-like: the only thing the customer
// carries back from the provider is their tenant identifier (the "iid" query
// param). The app-level apiKey/secret are configured server-side, so they are
// optional here and never sent from the browser.
export interface DirectErpConnectPayload {
    apiKey?: string;
    apiKeySecret?: string;
    installationId?: string;
    ownershipId?: string;
}

// Maps each direct ERP to the request body field its backend expects for the
// tenant identifier carried by the provider redirect (?iid=...).
export const DIRECT_ERP_IID_FIELD: Record<DirectErp, "ownershipId" | "installationId"> = {
    wisomeinbuero: "ownershipId",
    invoiz: "installationId",
};

// Connect using only the raw iid from the provider redirect. The iid is
// URL-encoded base64 and must be sent verbatim (no decode/trim).
export function connectDirectErpWithIid(erp: DirectErp, iid: string) {
    return connectDirectErp(erp, {[DIRECT_ERP_IID_FIELD[erp]]: iid});
}

// Frontend callback route per direct ERP. The provider redirects the customer
// here with `?iid=<tenant id>`.
export const DIRECT_ERP_CALLBACK_PATH: Record<DirectErp, string> = {
    wisomeinbuero: "/integrations/wisomeinbuero/callback",
    invoiz: "/integrations/invoiz/callback",
};

// When a customer arrives from the provider without an active SepaHeld session
// we cannot connect yet (the connect call is scoped to their customer id). We
// stash the provider + raw iid here so the connection can be resumed once they
// have logged in or registered. localStorage is used on purpose: it survives
// the provider redirect, the login/registration round-trip and onboarding.
export const PENDING_DIRECT_ERP_KEY = "pendingDirectErpConnection";

export interface PendingDirectErpConnection {
    erp: DirectErp;
    iid: string;
}

const isDirectErp = (value: unknown): value is DirectErp =>
    value === "invoiz" || value === "wisomeinbuero";

export function savePendingDirectErpConnection(erp: DirectErp, iid: string): void {
    try {
        localStorage.setItem(PENDING_DIRECT_ERP_KEY, JSON.stringify({erp, iid}));
    } catch {
        // Storage can be unavailable (private mode / quota); the connection
        // simply won't be resumed automatically in that case.
    }
}

export function getPendingDirectErpConnection(): PendingDirectErpConnection | null {
    try {
        const raw = localStorage.getItem(PENDING_DIRECT_ERP_KEY);
        if (!raw) {
            return null;
        }
        const parsed = JSON.parse(raw);
        if (parsed && isDirectErp(parsed.erp) && typeof parsed.iid === "string" && parsed.iid.length) {
            return {erp: parsed.erp, iid: parsed.iid};
        }
        return null;
    } catch {
        return null;
    }
}

export function clearPendingDirectErpConnection(): void {
    try {
        localStorage.removeItem(PENDING_DIRECT_ERP_KEY);
    } catch {
        // ignore
    }
}

// Builds the callback URL to resume a pending connection, or null when there is
// nothing to resume. The iid is kept verbatim (it is already URL-encoded base64)
// so the callback page forwards exactly what the provider sent.
export function pendingDirectErpCallbackUrl(): string | null {
    const pending = getPendingDirectErpConnection();
    if (!pending) {
        return null;
    }
    return `${DIRECT_ERP_CALLBACK_PATH[pending.erp]}?iid=${pending.iid}`;
}

export interface DirectErpSyncStatus {
    connected: boolean;
    lastSync: string | null;
    lastInvoiceId: string | null;
}

export interface DirectErpConnectResponse {
    apiKeyId: string;
    applicationType: string;
}

function endpoint(erp: DirectErp, suffix: string) {
    return `${erp}/core/${getCustomerId()}/${suffix}`;
}

export function connectDirectErp(erp: DirectErp, payload: DirectErpConnectPayload) {
    return post(endpoint(erp, "connect"), payload).then((response) => response.data as DirectErpConnectResponse);
}

export function disconnectDirectErp(erp: DirectErp) {
    return del(endpoint(erp, "connect")).then((response) => response.data);
}

export function getDirectErpSyncStatus(erp: DirectErp) {
    return get(endpoint(erp, "sync")).then((response) => response.data as DirectErpSyncStatus);
}

export function syncDirectErpInvoices(erp: DirectErp) {
    return post(endpoint(erp, "syncInvoices")).then((response) => response.data as DirectErpSyncStatus);
}
